/**
 * VortexTerminal — Marine co-navigator terminal with guided demo.
 *
 * Typewriter intro → 2 starter questions + Guided Demo option.
 * Chat mode: expert sailor co-navigator via /api/vortex-chat.
 * Demo mode: choose-your-own-adventure squall scenario.
 */
import { useState, useEffect, useRef, useCallback, type ReactElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useChat } from '../../hooks/useChat';
import {
  STORY, STORY_START,
  type StoryNode, type StoryLine, type LineStyle,
} from '../../data/vortex-guided-demo';

// ─── Terminal Configuration ──────────────────────────────

type IntroStyle = 'command' | 'system' | 'status' | 'text' | 'header'
  | 'feature' | 'detail' | 'diagram' | 'highlight' | 'blank';

interface TermLine {
  text: string;
  style: IntroStyle;
  charMs?: number;
  pauseMs?: number;
}

const CHAR_SPEED: Record<IntroStyle, number> = {
  command: 28, system: 14, status: 10, text: 18, header: 0,
  feature: 16, detail: 14, diagram: 0, highlight: 24, blank: 0,
};

const LINE_PAUSE: Record<IntroStyle, number> = {
  command: 400, system: 150, status: 80, text: 100, header: 200,
  feature: 100, detail: 50, diagram: 60, highlight: 300, blank: 60,
};

// ─── Intro Content ───────────────────────────────────────

const INTRO_LINES: TermLine[] = [
  { text: '$ vortex --copilot', style: 'command' },
  { text: '', style: 'blank' },
  { text: 'vortex v4.0.0 — navigator copilot', style: 'system' },
  { text: 'loading marine intelligence...', style: 'system', pauseMs: 500 },
  { text: '', style: 'blank' },
  { text: '  HRRR forecast    ··· LIVE (2.10kt MAE)', style: 'status' },
  { text: '  GFS forecast     ··· LIVE (2.81kt MAE)', style: 'status' },
  { text: '  ECMWF forecast   ··· LIVE (2.74kt MAE)', style: 'status' },
  { text: '  ensemble engine  ··· ONLINE', style: 'status' },
  { text: '  nowcast engine   ··· 6 MODELS BLENDED', style: 'status' },
  { text: '  NDBC stations    ··· 9/13 REPORTING', style: 'status' },
  { text: '', style: 'blank' },
  { text: '── NAVIGATOR COPILOT ──────────────────', style: 'header' },
  { text: '', style: 'blank' },
  { text: 'Your tactical weather intelligence.', style: 'text' },
  { text: 'Three NWP models compete per field.', style: 'text' },
  { text: 'When they disagree, that IS your', style: 'text' },
  { text: 'uncertainty signal.', style: 'text' },
  { text: '', style: 'blank' },
  { text: '  observe ──→ forecast ──→ decide ─╮', style: 'diagram' },
  { text: '    ╰──── calibrate ──→ learn ────╯', style: 'diagram' },
  { text: '', style: 'blank' },
  { text: '▸ ENSEMBLE · field-level competition', style: 'feature' },
  { text: '  Best model per metric per location.', style: 'detail' },
  { text: '', style: 'blank' },
  { text: '▸ NOWCAST · 6-model blend engine', style: 'feature' },
  { text: '  1-6h tactical. +22% accuracy.', style: 'detail' },
  { text: '', style: 'blank' },
  { text: '▸ CALIBRATION · EMOS post-processing', style: 'feature' },
  { text: '  35% CRPS improvement. Probabilistic.', style: 'detail' },
  { text: '  "12-18kt" beats "15kt" every time.', style: 'detail' },
  { text: '', style: 'blank' },
  { text: 'ready to navigate ◆', style: 'highlight' },
  { text: '', style: 'blank' },
];

// ─── Starter Questions ───────────────────────────────────

const STARTER_QUESTIONS = [
  'How does the ensemble pick which model to trust?',
  'What makes Vortex different from a standard weather app?',
];

// ─── Intro Line Rendering ────────────────────────────────

const INTRO_COLORS: Record<IntroStyle, string> = {
  command: 'text-cyan-400', system: 'text-gray-500', status: 'text-gray-400',
  text: 'text-gray-400', header: 'text-gray-600', feature: 'text-cyan-300',
  detail: 'text-gray-500', diagram: 'text-gray-600', highlight: 'text-white', blank: '',
};

function renderIntroLine(line: TermLine): ReactElement {
  const { text, style } = line;
  if (style === 'blank') return <>{'\u00A0'}</>;

  if (style === 'status') {
    const parts = text.split('···');
    if (parts.length === 2) {
      const word = parts[1].trim();
      const color = word.startsWith('LIVE') ? 'text-green-400'
        : word === 'ONLINE' ? 'text-green-400'
        : word.includes('BLENDED') ? 'text-amber-400'
        : word.includes('REPORTING') ? 'text-amber-400'
        : 'text-cyan-400';
      return <><span className="text-gray-600">{parts[0]}··· </span><span className={color}>{word}</span></>;
    }
  }
  if (style === 'command') {
    return <><span className="text-gray-600">$ </span><span className="text-cyan-400">{text.slice(2)}</span></>;
  }
  if (style === 'feature') {
    const match = text.match(/^(▸ )(\w+)( · )(.+)$/);
    if (match) {
      return <><span className="text-gray-600">{match[1]}</span><span className="text-cyan-400">{match[2]}</span><span className="text-gray-700">{match[3]}</span><span className="text-gray-300">{match[4]}</span></>;
    }
  }
  if (style === 'highlight') {
    return <><span className="text-white">{text.replace(' ◆', '')}</span><span className="text-cyan-400"> ◆</span></>;
  }
  return <>{text}</>;
}

// ─── Story Line Rendering ────────────────────────────────

const STORY_COLORS: Record<LineStyle, string> = {
  header: 'text-gray-300 font-semibold',
  data: 'text-cyan-300',
  narration: 'text-gray-400',
  alert: 'text-red-400',
  success: 'text-green-400',
  warning: 'text-yellow-400',
  dim: 'text-gray-600',
  accent: 'text-cyan-400',
  blank: '',
};

function renderStoryLine(line: StoryLine): ReactElement {
  if (line.style === 'blank') return <>{'\u00A0'}</>;
  return <>{line.text}</>;
}

// ─── Follow-up Parser ────────────────────────────────────

function parseFollowUps(content: string): { text: string; questions: string[] } {
  const lines = content.split('\n');
  const questions: string[] = [];
  const textLines: string[] = [];
  for (const line of lines) {
    if (line.trim().startsWith('?> ')) {
      questions.push(line.trim().slice(3));
    } else {
      textLines.push(line);
    }
  }
  return { text: textLines.join('\n').trimEnd(), questions };
}

// ─── Story Node Component ────────────────────────────────

const StoryNodeView = ({
  node,
  isLatest,
  onChoice,
}: {
  node: StoryNode;
  isLatest: boolean;
  onChoice: (next: string) => void;
}) => (
  <div className="mt-2">
    {node.lines.map((line, i) => (
      <motion.div
        key={`${node.id}-${i}`}
        initial={isLatest ? { opacity: 0, x: -4 } : false}
        animate={{ opacity: 1, x: 0 }}
        transition={isLatest ? { delay: i * 0.06, duration: 0.2 } : undefined}
        className={`${STORY_COLORS[line.style]} whitespace-pre`}
      >
        {renderStoryLine(line)}
      </motion.div>
    ))}

    {isLatest && node.choices && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: node.lines.length * 0.06 + 0.3 }}
        className="mt-2"
      >
        <div className="text-gray-700 text-[11px] font-mono mb-1">your call, skipper:</div>
        {node.choices.map((choice) => (
          <button
            key={choice.next}
            onClick={() => onChoice(choice.next)}
            className="group block w-full text-left py-1 text-gray-400 hover:text-cyan-400 transition-all duration-150 cursor-pointer"
          >
            <span className="text-gray-700 group-hover:text-cyan-600 transition-colors">{'  › '}</span>
            <span className="group-hover:underline underline-offset-2 decoration-cyan-800">{choice.label}</span>
            <span className="text-[10px] text-gray-700 ml-2 group-hover:text-gray-500">[{choice.tag}]</span>
          </button>
        ))}
      </motion.div>
    )}
  </div>
);

// ─── Main Component ──────────────────────────────────────

export const VortexTerminal = () => {
  const termBodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Intro typing state
  const [completedLines, setCompletedLines] = useState(1);
  const [currentChars, setCurrentChars] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [typingDone, setTypingDone] = useState(false);
  const timerRef = useRef<number>(0);

  // Mode: 'select' (choosing), 'chat', or 'demo'
  const [mode, setMode] = useState<'select' | 'chat' | 'demo'>('select');

  // Chat state
  const [chatInput, setChatInput] = useState('');
  const { messages, isLoading, streamingContent, sendMessage, clearChat } = useChat('/api/vortex-chat');
  const hasMessages = messages.length > 0 || !!streamingContent;

  // Demo state
  const [demoHistory, setDemoHistory] = useState<string[]>([]);

  // Cursor blink
  useEffect(() => {
    const id = setInterval(() => setShowCursor(v => !v), 530);
    return () => clearInterval(id);
  }, []);

  // Focus input
  useEffect(() => {
    if (typingDone && mode === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [typingDone, mode]);

  // Auto-scroll
  useEffect(() => {
    if (termBodyRef.current) {
      termBodyRef.current.scrollTop = termBodyRef.current.scrollHeight;
    }
  }, [messages, streamingContent, completedLines, currentChars, demoHistory]);

  // Typing engine
  useEffect(() => {
    let lineIdx = 1;
    let charIdx = 0;
    let cancelled = false;

    const advance = () => {
      if (cancelled || lineIdx >= INTRO_LINES.length) { setTypingDone(true); return; }
      const line = INTRO_LINES[lineIdx];
      const speed = line.charMs ?? CHAR_SPEED[line.style];
      const pause = line.pauseMs ?? LINE_PAUSE[line.style];

      if (speed === 0 || line.text.length === 0) {
        lineIdx++;
        setCompletedLines(lineIdx);
        setCurrentChars(0);
        charIdx = 0;
        timerRef.current = window.setTimeout(advance, pause);
        return;
      }
      charIdx++;
      setCurrentChars(charIdx);
      if (charIdx >= line.text.length) {
        lineIdx++;
        setCompletedLines(lineIdx);
        setCurrentChars(0);
        charIdx = 0;
        timerRef.current = window.setTimeout(advance, pause);
      } else {
        timerRef.current = window.setTimeout(advance, speed);
      }
    };
    timerRef.current = window.setTimeout(advance, 600);
    return () => { cancelled = true; clearTimeout(timerRef.current); };
  }, []);

  // Handlers
  const handleSend = useCallback((text: string) => {
    if (!text.trim() || isLoading) return;
    sendMessage(text);
    setChatInput('');
  }, [sendMessage, isLoading]);

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); handleSend(chatInput); };

  const startDemo = useCallback(() => {
    setMode('demo');
    setDemoHistory([STORY_START]);
  }, []);

  const handleDemoChoice = useCallback((next: string) => {
    setDemoHistory(prev => [...prev, next]);
  }, []);

  const handleResetDemo = useCallback(() => {
    setDemoHistory([STORY_START]);
  }, []);

  const handleStarterClick = useCallback((q: string) => {
    setMode('chat');
    // Small delay so chat mode renders before sending
    setTimeout(() => sendMessage(q), 50);
  }, [sendMessage]);

  const currentLine = INTRO_LINES[completedLines];
  const isTyping = currentLine && currentChars > 0;

  const cursor = (
    <span
      className={`inline-block w-[0.55em] h-[1.15em] bg-cyan-400/80 align-text-bottom ml-px ${showCursor ? 'opacity-100' : 'opacity-0'}`}
      style={{ transition: 'opacity 0.1s' }}
    />
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 p-4 md:p-6">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Link to="/" className="text-sm font-mono text-gray-500 hover:text-gray-300 transition-colors">&larr; Kempion</Link>
          <div className="flex items-center gap-2">
            <motion.div className="w-2 h-2 rounded-full bg-green-500" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }} />
            <span className="text-xs font-mono text-accent/60 uppercase tracking-widest">Vortex</span>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="min-h-screen flex flex-col items-center justify-center px-4 py-20">
        <div className="w-full max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="rounded-xl border border-gray-800/80 overflow-hidden shadow-2xl shadow-cyan-900/10"
          >
            {/* Title Bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-900/80 border-b border-gray-800/50">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500/60" />
                <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <span className="w-3 h-3 rounded-full bg-green-500/60" />
              </div>
              <span className="text-[11px] text-gray-600 font-mono ml-2">vortex — navigator</span>
              {mode === 'chat' && hasMessages && (
                <div className="ml-auto flex items-center gap-3">
                  <button onClick={clearChat} className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono">clear</button>
                </div>
              )}
              {mode === 'demo' && (
                <div className="ml-auto flex items-center gap-3">
                  <button onClick={handleResetDemo} className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors font-mono">restart scenario</button>
                </div>
              )}
            </div>

            {/* Terminal Body */}
            <div
              ref={termBodyRef}
              className="bg-[#080808] px-5 py-5 md:px-6 md:py-6 font-mono text-[12px] md:text-[13px] leading-[1.7] min-h-[420px] max-h-[650px] overflow-y-auto overflow-x-auto"
              role="log"
              aria-label="Vortex navigator terminal"
            >
              {/* Intro lines */}
              {INTRO_LINES.slice(0, completedLines).map((line, i) => (
                <div key={i} className={`${INTRO_COLORS[line.style]} whitespace-pre`}>{renderIntroLine(line)}</div>
              ))}
              {isTyping && (
                <div className={`${INTRO_COLORS[currentLine.style]} whitespace-pre`}>
                  {currentLine.text.slice(0, currentChars)}{cursor}
                </div>
              )}

              {/* Post-typing content */}
              {typingDone && !isTyping && (
                <>
                  {/* Mode selection */}
                  {mode === 'select' && (
                    <div className="mt-1">
                      <div className="text-gray-600 whitespace-pre">{'── ready, skipper ─────────────────────'}</div>
                      <div className="text-gray-600 whitespace-pre">{'\u00A0'}</div>
                      {STARTER_QUESTIONS.map((q) => (
                        <button key={q} onClick={() => handleStarterClick(q)} className="group block w-full text-left py-1 text-gray-400 hover:text-cyan-400 transition-all duration-150 cursor-pointer">
                          <span className="text-gray-700 group-hover:text-cyan-600 transition-colors">{'  › '}</span>
                          <span className="group-hover:underline underline-offset-2 decoration-cyan-800">{q}</span>
                        </button>
                      ))}
                      <div className="text-gray-600 whitespace-pre">{'\u00A0'}</div>
                      <button onClick={startDemo} className="group block w-full text-left py-1 text-amber-400/80 hover:text-amber-300 transition-all duration-150 cursor-pointer">
                        <span className="text-amber-600 group-hover:text-amber-500 transition-colors">{'  ⚡ '}</span>
                        <span className="group-hover:underline underline-offset-2 decoration-amber-700">Guided Demo — Navigate a squall in the Atlantic</span>
                        <span className="text-[10px] text-gray-700 ml-2">[INTERACTIVE]</span>
                      </button>
                    </div>
                  )}

                  {/* ── CHAT MODE ── */}
                  {mode === 'chat' && (
                    <>
                      {messages.map((msg, msgIdx) => {
                        if (msg.role === 'user') {
                          return (
                            <div key={msg.id} className="mt-1 whitespace-pre">
                              <span className="text-gray-600">$ </span>
                              <span className="text-cyan-400">{msg.content}</span>
                            </div>
                          );
                        }
                        const { text, questions } = parseFollowUps(msg.content);
                        const isLast = msgIdx === messages.length - 1;
                        return (
                          <div key={msg.id} className="mt-1">
                            <div className="whitespace-pre-wrap text-gray-400 pl-2">{text}</div>
                            {isLast && questions.length > 0 && !isLoading && (
                              <div className="mt-2 pl-2">
                                <div className="text-gray-700 text-[11px] font-mono mb-1">follow up:</div>
                                {questions.map((q) => (
                                  <button key={q} onClick={() => handleSend(q)} className="group block w-full text-left py-0.5 text-gray-500 hover:text-cyan-400 transition-all duration-150 cursor-pointer">
                                    <span className="text-gray-700 group-hover:text-cyan-600 transition-colors">{'  › '}</span>
                                    <span className="group-hover:underline underline-offset-2 decoration-cyan-800">{q}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {streamingContent && (
                        <div className="mt-1 whitespace-pre-wrap text-gray-400 pl-2">
                          {streamingContent}
                          <span className="inline-block w-[0.55em] h-[1.15em] bg-cyan-400/80 align-text-bottom ml-px animate-pulse" />
                        </div>
                      )}

                      <form onSubmit={handleSubmit} className="flex items-center mt-1">
                        <span className="text-gray-600">$ </span>
                        {!isLoading && !chatInput && cursor}
                        <input
                          ref={inputRef}
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          placeholder={isLoading ? '' : 'talk to your navigator...'}
                          disabled={isLoading}
                          className="flex-1 bg-transparent text-cyan-400 placeholder:text-gray-700 outline-none disabled:opacity-50 ml-1 caret-cyan-400"
                        />
                        {isLoading && (
                          <span className="flex items-center gap-1 ml-2">
                            <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                            <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                            <span className="w-1 h-1 bg-cyan-400 rounded-full animate-bounce" />
                          </span>
                        )}
                      </form>
                    </>
                  )}

                  {/* ── DEMO MODE ── */}
                  {mode === 'demo' && (
                    <AnimatePresence>
                      {demoHistory.map((nodeId, idx) => {
                        const node = STORY[nodeId];
                        if (!node) return null;
                        return (
                          <StoryNodeView
                            key={`${nodeId}-${idx}`}
                            node={node}
                            isLatest={idx === demoHistory.length - 1}
                            onChoice={handleDemoChoice}
                          />
                        );
                      })}
                    </AnimatePresence>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      {/* Background glow — blue tint for ocean theme */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px] opacity-30" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px] opacity-20" />
      </div>
    </div>
  );
};
